import Icon from '@/features/analytics/invite-links/ui/Marking/icon'
import { useInviteLinksStore } from '@/features/analytics/model'
import { useUpdateMarkingFormData } from '@/shared/api'
import { QuestionSVGIcon } from '@/shared/assets'
import ContragentCard from '@/shared/components/contragent-card'
import { Checkbox, DisableTooltip } from '@/shared/ui'
import { Label } from '@/shared/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { ORDMarkingFormData } from '@synapex/prisma/client'
import {
    WorkspaceWithLinkDescriptor,
    workspaceWithLinkMethods,
} from '@synapex/shared2/entities'
import { ContragentResponse } from '@synapex/shared2/gateway-rest-api'
import { useState } from 'react'

type PublisherProps = {
    markingFormData: ORDMarkingFormData
} & WorkspaceWithLinkDescriptor

export const Publisher: React.FC<PublisherProps> = ({
    markingFormData,
    ...props
}) => {
    const wwld = workspaceWithLinkMethods.pickWwld(props)

    const { publisherError, setPublisherError, setPublishOptionError } =
        useInviteLinksStore()

    const [error, setError] = useState(false)

    const updateMarkingFormData = useUpdateMarkingFormData(wwld)

    function handleChangeCheckboxStatus() {
        if (!markingFormData.ord_contragent_id) {
            setError(true)
            return
        }

        if (isBlockedByContract) {
            return
        }

        if (isBlockedByPublishOption) {
            setPublishOptionError(true)
            return
        }

        updateMarkingFormData.mutate({
            publisher_flag: !markingFormData.publisher_flag,
        })

        setPublisherError(false)
    }

    function handleSelectContragent(contragent: ContragentResponse | null) {
        updateMarkingFormData.mutate({
            ord_contragent_id: contragent ? contragent.ord_contragent_id : null,
        })
    }

    const isBlockedByContract =
        !!markingFormData.draft_contract_id ||
        !!markingFormData.custom_contract_id

    const isBlockedByPublishOption = !markingFormData.publish_option_flag

    return (
        <div className="flex flex-col gap-[6px]">
            <div className="flex gap-9">
                <div className="flex items-center gap-[6px]">
                    <Icon
                        icon="profile"
                        checked={markingFormData.publisher_flag}
                    />

                    <h2 className="text-sm font-semibold">Издатель (РР)</h2>

                    <Tooltip>
                        <TooltipTrigger>
                            <QuestionSVGIcon className="cursor-pointer" />
                        </TooltipTrigger>
                        <TooltipContent side="top">
                            <p>Информация о Издателе (РР)</p>
                        </TooltipContent>
                    </Tooltip>
                </div>
            </div>

            <div className="flex items-end justify-between gap-8">
                <div className="flex w-[448px] items-center gap-2">
                    <div className="relative flex flex-col gap-[6px]">
                        <span className="text-[10px] text-synapex-light-gray">
                            Название (ФИО)
                        </span>

                        <ContragentCard
                            {...wwld}
                            onSelectContragent={handleSelectContragent}
                            error={error}
                            setError={setError}
                            roleType="publisher"
                            currentContragentId={
                                markingFormData.ord_contragent_id
                            }
                            allowEdit={!markingFormData.publisher_flag}
                            tooltipMessage="Если хотите отредактировать, то уберите галочку справа"
                        />
                    </div>
                </div>

                <div className="flex w-[105px] items-end gap-[6px] pb-0">
                    <DisableTooltip
                        enabled={
                            !markingFormData.ord_contragent_id ||
                            isBlockedByContract ||
                            isBlockedByPublishOption
                        }
                        className="h-5"
                        message={
                            isBlockedByContract
                                ? 'Нельзя отжать так как договор уже сформирован'
                                : 'Пожалуйста, заполните все параметры, чтобы отметить этот пункт'
                        }
                        asChild
                    >
                        <div
                            onClick={() => {
                                if (
                                    !markingFormData.ord_contragent_id ||
                                    isBlockedByContract ||
                                    isBlockedByPublishOption
                                ) {
                                    handleChangeCheckboxStatus()
                                }
                            }}
                        >
                            <Checkbox
                                id="publisher_allow"
                                checked={markingFormData.publisher_flag}
                                disabled={
                                    !markingFormData.ord_contragent_id ||
                                    isBlockedByContract ||
                                    isBlockedByPublishOption
                                }
                                onChange={handleChangeCheckboxStatus}
                                sizeVariant="big"
                                variant={publisherError ? 'error' : 'green'}
                                size={20}
                            />
                        </div>
                    </DisableTooltip>
                    <Label
                        htmlFor="publisher_allow"
                        className="w-[79px] text-[10px] font-medium leading-3"
                    >
                        Издатель добавлен
                    </Label>
                </div>
            </div>
        </div>
    )
}
